import { Box, VStack, Spinner, Center, Text } from "@chakra-ui/react";
import { useEffect, useLayoutEffect, useRef, useCallback, useState } from "react";
import {
  useLunaMessages,
  useLunaIsThinking,
  useLunaPagination,
  useLunaToolUpdateCount,
  useLunaStore,
} from "state/luna";
import { LunaMessage } from "./LunaMessage";
import { LunaThinkingIndicator } from "./LunaThinkingIndicator";

interface LunaMessageThreadProps {
  sessionId: string;
  readOnly?: boolean;
  px?: string;
  pt?: string;
}

export const LunaMessageThread = ({ sessionId, readOnly, px = "md", pt = "lg" }: LunaMessageThreadProps) => {
  const messages = useLunaMessages(sessionId);
  const isThinking = useLunaIsThinking(sessionId);
  const { hasMore, isLoadingMore } = useLunaPagination(sessionId);
  const toolUpdateCount = useLunaToolUpdateCount(sessionId);
  const loadMoreMessages = useLunaStore((state) => state.loadMoreMessages);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Simple state tracking
  const [isReady, setIsReady] = useState(false);
  const isNearBottomRef = useRef(true);
  const isLoadingOlderRef = useRef(false);
  const lastMessageIdRef = useRef<string | null>(null);

  // Scroll anchor for pagination - stores the message ID to anchor to
  const scrollAnchorRef = useRef<{
    messageId: string;
    offsetFromTop: number;
  } | null>(null);

  // Helper to scroll to bottom
  const scrollToBottom = useCallback((instant = false) => {
    const container = containerRef.current;
    if (!container) return;

    const targetScrollTop = container.scrollHeight - container.clientHeight;
    if (instant) {
      container.scrollTop = targetScrollTop;
    } else {
      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    }
  }, []);

  // Helper to check if near bottom
  const checkIfNearBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;

    const threshold = 150;
    const scrollBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return scrollBottom < threshold;
  }, []);

  // Initial scroll to bottom when first messages load
  useEffect(() => {
    if (isReady || messages.length === 0) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom(true);
        setIsReady(true);
      });
    });
  }, [messages.length, scrollToBottom, isReady]);

  // Auto-scroll when new content arrives (new messages, tool updates)
  useEffect(() => {
    if (!isReady || isLoadingOlderRef.current) return;

    const lastMessage = messages[messages.length - 1];
    const currentLastId = lastMessage?.id ?? null;
    const previousLastId = lastMessageIdRef.current;

    // Check if a new message was added at the end
    const newMessageAdded = currentLastId !== previousLastId && currentLastId !== null;
    lastMessageIdRef.current = currentLastId;

    // Scroll to bottom if: near bottom OR a new message was sent/received
    if (isNearBottomRef.current || newMessageAdded) {
      // When a new message triggers scroll, resume auto-scrolling
      if (newMessageAdded) {
        isNearBottomRef.current = true;
      }
      requestAnimationFrame(() => {
        scrollToBottom(false);
      });
    }
  }, [messages, isThinking, toolUpdateCount, isReady, scrollToBottom]);

  // ResizeObserver for content size changes (Collapse animations, etc.)
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isReady) return;

    const observer = new ResizeObserver(() => {
      if (isLoadingOlderRef.current) return;
      if (isNearBottomRef.current) {
        scrollToBottom(false);
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [isReady, scrollToBottom]);

  // Scroll event handler
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || isLoadingOlderRef.current) return;

    // Track if user is near bottom
    isNearBottomRef.current = checkIfNearBottom();

    // Trigger pagination when near top
    if (!hasMore || isLoadingMore) return;

    if (container.scrollTop < 200 && messages.length > 0) {
      isLoadingOlderRef.current = true;

      // Save the first message as our scroll anchor
      const firstMessage = messages[0];
      const firstMessageEl = document.querySelector(
        `[data-message-id="${firstMessage.id}"]`
      );

      if (firstMessageEl) {
        const containerRect = container.getBoundingClientRect();
        const messageRect = firstMessageEl.getBoundingClientRect();
        scrollAnchorRef.current = {
          messageId: firstMessage.id,
          offsetFromTop: messageRect.top - containerRect.top,
        };
      }

      loadMoreMessages(sessionId);
    }
  }, [hasMore, isLoadingMore, loadMoreMessages, sessionId, checkIfNearBottom, messages]);

  // Restore scroll position after older messages are loaded
  // useLayoutEffect runs before paint, so user won't see a jump
  useLayoutEffect(() => {
    if (!scrollAnchorRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const { messageId, offsetFromTop } = scrollAnchorRef.current;
    const anchorEl = document.querySelector(`[data-message-id="${messageId}"]`);

    if (anchorEl) {
      const containerRect = container.getBoundingClientRect();
      const anchorRect = anchorEl.getBoundingClientRect();
      const currentOffset = anchorRect.top - containerRect.top;
      const adjustment = currentOffset - offsetFromTop;

      container.scrollTop += adjustment;
    }

    scrollAnchorRef.current = null;

    // Reset loading flag after a frame to let other effects settle
    requestAnimationFrame(() => {
      isLoadingOlderRef.current = false;
    });
  }, [messages]);

  // Scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Reset state when session changes
  useEffect(() => {
    setIsReady(false);
    isNearBottomRef.current = true;
    isLoadingOlderRef.current = false;
    scrollAnchorRef.current = null;
    lastMessageIdRef.current = null;
  }, [sessionId]);

  // Check if tools are currently running
  const hasRunningTools = messages.some((msg) =>
    msg.tool_executions?.some(
      (exec) => exec.status === "running" || exec.status === "pending"
    )
  );

  const showThinkingIndicator = isThinking && !hasRunningTools;
  const showContent = isReady || (messages.length === 0 && !isLoadingMore);

  return (
    <Box
      ref={containerRef}
      position="absolute"
      inset={0}
      overflowY="auto"
      px={px}
      pt={pt}
      pb="lg"
      visibility={showContent ? "visible" : "hidden"}
    >
      <VStack
        ref={contentRef}
        align="stretch"
        spacing={0}
        maxW="800px"
        mx="auto"
        pb="xl"
      >
        {/* Loading indicator */}
        {isLoadingMore && (
          <Center py="md">
            <Spinner size="sm" color="p.600" />
          </Center>
        )}

        {/* Load more hint */}
        {hasMore && !isLoadingMore && (
          <Center py="sm">
            <Text fontSize="xs" color="text.body.tertiary">
              Scroll up to load earlier messages
            </Text>
          </Center>
        )}

        {messages
          .filter((message) => {
            // Hide tool responses that have a tool_execution_id
            // since they're already shown in the tool execution dropdown
            if (message.role === "tool" && message.tool_execution_id) {
              return false;
            }
            return true;
          })
          .map((message) => (
            <LunaMessage
              key={message.id}
              message={message}
              sessionId={sessionId}
              readOnly={readOnly}
              data-message-id={message.id}
            />
          ))}

        {showThinkingIndicator && (
          <Box pb={readOnly ? "xl" : "100px"}>
            <LunaThinkingIndicator />
          </Box>
        )}
      </VStack>
    </Box>
  );
};
